import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  connectMessagesSocket, disconnectMessagesSocket, onMessagesEvent,
} from "@/lib/messagesSocket";

const REFRESH_EVENTS = ["ready", "resume", "message", "read", "message_deleted", "conversation"];

/** Keeps the messenger socket alive for the session, returns the total unread
 *  count for nav badges, mirrors it to the home-screen icon badge, and pops a
 *  texting-style banner for messages arriving in chats you're not looking at. */
export function useMessagesUnread(enabled, user) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const q = useQuery({
    queryKey: ["messages-unread"],
    queryFn: async () => (await api.get("/messages/unread-count")).data.unread,
    enabled: !!enabled,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    connectMessagesSocket();
    const off = onMessagesEvent((ev) => {
      if (!REFRESH_EVENTS.includes(ev?.type)) return;
      qc.invalidateQueries({ queryKey: ["messages-unread"] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    });
    return () => { off(); disconnectMessagesSocket(); };
  }, [enabled, qc]);

  // In-app banner for new messages when that chat isn't open on screen.
  useEffect(() => {
    if (!enabled || !user) return undefined;
    return onMessagesEvent((ev) => {
      if (ev?.type !== "message" || ev.message?.kind === "system") return;
      const m = ev.message;
      if (m.sender_id === user.id) return;
      if (window.location.pathname === `/messages/${ev.conversation_id}` && document.visibilityState === "visible") return;
      const conv = (qc.getQueryData(["conversations"]) || []).find((c) => c.id === ev.conversation_id);
      const title = conv?.type === "group" ? `${m.sender_name} · ${conv.name}` : m.sender_name;
      const body = m.timer === "read" ? "Sent you a message" : (m.text || (m.video_url ? "🎬 Video" : m.image_url ? "📷 Photo" : ""));
      try { navigator.vibrate?.(40); } catch (e) { /* not supported */ }
      toast(title, {
        id: `msg-${ev.conversation_id}`,
        description: body.length > 120 ? `${body.slice(0, 120)}…` : body,
        duration: 6000,
        action: { label: "Open", onClick: () => navigate(`/messages/${ev.conversation_id}`) },
      });
    });
  }, [enabled, user, qc, navigate, location.pathname]);

  // Home-screen app icon badge (installed PWA on iOS 16.4+ / Android).
  const count = q.data || 0;
  useEffect(() => {
    if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    const p = enabled && count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge();
    p?.catch?.(() => {});
  }, [count, enabled]);
  return count;
}
